import {
  collection,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
} from "firebase/firestore";
import { db } from "./Firebase"; // Adjust the path as necessary
import { WhereFilterOp, OrderByDirection } from "firebase/firestore";

export interface QueryOptions {
  fieldPath: string;
  opStr: WhereFilterOp;
  value: any;
}

class FirebaseDao {
  async create(collectionName: string, data: any): Promise<string> {
    const colRef = collection(db, collectionName);
    const docRef = await addDoc(colRef, data);
    return docRef.id;
  }

  async read(collectionName: string, documentId: string): Promise<any> {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Document not found");
    }

    return { id: docSnap.id, ...docSnap.data() };
  }

  async readAll(collectionName: string): Promise<any[]> {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  async update(
    collectionName: string,
    documentId: string,
    data: any
  ): Promise<void> {
    const docRef = doc(db, collectionName, documentId);
    return await updateDoc(docRef, data);
  }

  async delete(collectionName: string, documentId: string): Promise<void> {
    const docRef = doc(db, collectionName, documentId);
    return await deleteDoc(docRef);
  }

  async query(
    collectionName: string,
    queryOptions: QueryOptions[],
    limitNum?: number,
    orderByField?: string,
    orderDirection?: OrderByDirection
  ): Promise<any[]> {
    let q = query(collection(db, collectionName));

    queryOptions.forEach((option) => {
      if (option.value) {
        q = query(q, where(option.fieldPath, option.opStr, option.value));
      }
    });

    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }

    if (limitNum) {
      q = query(q, limit(limitNum));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  async count(
    collectionName: string,
    queryOptions: QueryOptions[]
  ): Promise<{ filter: string; count: number }> {
    let q = query(collection(db, collectionName));

    queryOptions.forEach((option) => {
      q = query(q, where(option.fieldPath, option.opStr, option.value));
    });

    const querySnapshot = await getDocs(q);
    return { filter: queryOptions[0].value, count: querySnapshot.docs.length };
  }
}

export default new FirebaseDao();
